/**
 * Comparing other pricing points on one hub's chart.
 *
 * Every chart in a hub's workspace can draw other points beside the hub's own
 * — TradingView's "Compare symbol": a Compare control in the chart's header,
 * a removable chip per compared point, and one more set of lines per point.
 * The comparison belongs to the CHART, not to a global basket, because a
 * point that has no forward curve can never be on a Strips chart and one with
 * no settlement node can never be on an Index chart. So each chart offers
 * only the points that can draw it (`comparableHubs`).
 *
 * **How the lines are told apart.** A chart already spends colour and stroke:
 * Strips colours by tenor and dashes by block, Trends colours by market. A
 * comparison needs the point to be the loudest channel, so while comparing:
 *
 * - colour belongs to the POINT (the hub first, then each compared point);
 * - whatever colour used to separate becomes a SHADE of the point's colour —
 *   the first tenor or market selected full strength, later ones lighter;
 * - stroke (the block) is unchanged.
 *
 * Every selection the chart already had is kept: two points on five tenors
 * draw ten lines. The tooltip groups them by point.
 *
 * Comparisons are saved with the chart's view (`config.compare`), so a view
 * such as "West Hub vs SP15" can be saved and pinned like any other.
 */

import { pointSettles } from "./topology";

import type { ChartSeries } from "@/components/market-intelligence/charts/chart-data";
import type { ChartColorToken } from "@/components/market-intelligence/charts/chart-theme";
import type { MarketHub } from "@/types/market-intelligence";

/**
 * Point colours while comparing, by position: the hub is the first, compared
 * points take the rest in the order they were added. The app-wide categorical
 * ramp — the market-term ramp already means "tenor" on these charts.
 */
export const COMPARE_POINT_TOKENS: readonly ChartColorToken[] = [
  "chart-1",
  "chart-2",
  "chart-3",
  "chart-4",
  "chart-5",
];

/** How many OTHER points one chart compares — one colour each after the hub's. */
export const MAX_COMPARE = COMPARE_POINT_TOKENS.length - 1;

/** Which data a chart draws, and so which points can be compared on it. */
export type CompareSurface = "forwards" | "index" | "hourly";

/**
 * Whether a point can draw on a surface at all — the same rule the point's
 * own tabs read (`tabAppliesTo` in `hub-tabs.tsx`): Forwards needs a curve,
 * Index a settlement node, and Hourly a power settlement node (gas settles
 * one price a day).
 */
export function canCompareOn(surface: CompareSurface, hub: MarketHub): boolean {
  if (surface === "forwards") return hub.curveLocation !== null;
  if (surface === "hourly") return hub.commodity === "power" && pointSettles(hub);
  return pointSettles(hub);
}

/**
 * The points a chart may offer: same commodity as the hub (power and gas
 * quote in different units), able to draw this surface, and not the hub
 * itself. A–Z within region; regions in feed order.
 */
export function comparableHubs(
  all: readonly MarketHub[],
  hub: MarketHub,
  surface: CompareSurface,
): MarketHub[] {
  return all.filter(
    (h) => h.commodity === hub.commodity && h.code !== hub.code && canCompareOn(surface, h),
  );
}

/**
 * The saved list, resolved against what the chart can offer, in the order
 * the reader added them. A code the chart cannot draw — the feed dropped it,
 * or a view was saved on another hub's chart — is skipped rather than shown
 * as a line that never appears.
 */
export function resolveCompared(
  codes: readonly string[],
  candidates: readonly MarketHub[],
): MarketHub[] {
  return codes
    .map((code) => candidates.find((h) => h.code === code))
    .filter((h): h is MarketHub => h !== undefined)
    .slice(0, MAX_COMPARE);
}

/**
 * Adds or removes one code, keeping order and the cap. With a cap of one,
 * picking another point swaps it in: one point at a time is a choice, not a
 * list to make room in.
 */
export function toggleCompared(codes: readonly string[], code: string, max: number = MAX_COMPARE): string[] {
  if (codes.includes(code)) return codes.filter((c) => c !== code);
  if (max === 1) return [code];
  if (codes.length >= max) return [...codes];
  return [...codes, code];
}

/**
 * The strength of the i-th of n shades: full for the first, evenly lighter
 * after, never fainter than 0.3 — fainter than that and a line disappears on
 * the dark theme.
 */
export function compareShade(index: number, count: number): number {
  if (index <= 0 || count <= 1) return 1;
  const step = Math.min(0.25, 0.7 / (count - 1));
  return Math.max(0.3, Math.round((1 - index * step) * 100) / 100);
}

/** One point's lines, as its chart would draw them on their own. */
export interface ComparePointLines {
  code: string;
  name: string;
  lines: readonly ChartSeries[];
  /** False while its data is still on the way — not yet "no data". */
  loaded: boolean;
}

/**
 * Restyles each point's lines for one chart — see the module note. The hub
 * comes first in `points`.
 *
 * Returns the series to draw and the names of compared points that drew
 * nothing once loaded, so the chart can say so instead of leaving a reader to
 * wonder where a point went.
 */
export function compareSeries(points: readonly ComparePointLines[]): {
  series: ChartSeries[];
  empty: string[];
} {
  const series: ChartSeries[] = [];
  const empty: string[] = [];

  points.forEach((point, index) => {
    const visible = point.lines.filter((l) => l.visible !== false);
    // The colours the chart would have used, in first-seen order, become the
    // shades — so the first tenor or market selected stays full strength. A
    // line's own shade counts as part of its colour: quarterly strips share a
    // colour per quarter and tell their years apart by shade (`stripStyles`),
    // and keying on the colour alone gave those years one shade.
    const look = (l: ChartSeries) => `${l.color}|${l.opacity ?? 1}`;
    const shades = [...new Set(visible.map(look))];
    const single = visible.length === 1;

    for (const line of point.lines) {
      series.push({
        ...line,
        id: `${point.code}:${line.id}`,
        // A point with one line needs only its name; with several, each line
        // keeps its own label after it, and the tooltip groups them.
        label: single ? point.name : `${point.name} · ${line.label}`,
        group: single ? undefined : point.name,
        color: COMPARE_POINT_TOKENS[index % COMPARE_POINT_TOKENS.length],
        opacity: compareShade(shades.indexOf(look(line)), shades.length),
      });
    }

    const drew = visible.some((l) => l.points.some((p) => p.value !== null));
    if (index > 0 && point.loaded && !drew) empty.push(point.name);
  });

  return { series, empty };
}

/** A compared point's swatch for DOM chips, by its position after the hub. */
export function comparedPointColor(position: number): string {
  return `var(--${COMPARE_POINT_TOKENS[(position + 1) % COMPARE_POINT_TOKENS.length]})`;
}

/** The hub's own swatch while comparing. */
export const COMPARE_HUB_COLOR = `var(--${COMPARE_POINT_TOKENS[0]})`;

/**
 * What each line is called: the point's name, plus its region where two of
 * the points on the chart share a name — the feed has more than one "North".
 */
export function compareNames(hubs: readonly MarketHub[]): Map<string, string> {
  const counts = new Map<string, number>();
  for (const h of hubs) counts.set(h.name, (counts.get(h.name) ?? 0) + 1);
  return new Map(
    hubs.map((h) => [h.code, (counts.get(h.name) ?? 0) > 1 ? `${h.name} (${h.iso})` : h.name]),
  );
}
